<template>
  <v-row no-gutters>
    <v-col>
      <ReportHeading
        label="Звіт по роботах"
        :loading="loading"
        :disabled="!filterReady"
        class="no-print"
        @update-report="updateReport"
      >
        <template v-slot:action>
          <PrintModeBtn
            tooltip-text="Режим звіту"
            @click="changePrintMode"
          />
          <DownloadFile
            location="xlsx"
            :params="specificationData"
            filename="Специфікація"
            extension="xlsx"
            tooltip="Завантажити специфікацію у форматі Excel"
          />
          <DownloadFile
            location="xlsx"
            :params="reportData"
            filename="Звіт"
            extension="xlsx"
            tooltip="Завантажити звіт у форматі Excel"
          />
        </template>
        <template v-slot:filters>
          <ReportFilter
            v-model="filter"
            :is-dense="true"
            class="py-0"
            @reset="handleFilterReset"
          />
          <v-row
            align="center"
            dense
            class="pb-3"
          >
            <v-col cols="4">
              <Search
                v-model="search"
                title="Пошук за кодом задачі"
                clearable
                placeholder="Введіть код задачі"
              />
            </v-col>
            <v-col cols="4">
              <v-autocomplete
                v-model="authorFilter"
                :items="authors"
                clearable
                hide-no-data
                hide-selected
                hide-details
                label="Фільтр за автором"
                placeholder="Введіть автора"
                return-object
                dense
                outlined
              />
            </v-col>
            <v-col cols="4">
              <v-autocomplete
                v-model="employeeFilter"
                :items="employees"
                clearable
                hide-no-data
                hide-selected
                dense
                hide-details
                label="Фільтр за виконавцем"
                placeholder="Введіть виконавця"
                return-object
                outlined
              />
            </v-col>
          </v-row>
        </template>
      </ReportHeading>
      <Totals
        class="no-print"
        :source="filteredIssues"
        @payedChange="updateTotalPayed"
        @spentChange="updateTotalSpent"
      />

      <PrintForm
        v-if="printMode"
        :items="filteredIssues"
        :filter="filter"
        :update-total-payed="totalPayed"
        :update-total-spent="totalSpent"
        :issues="issues"
        :projects="projects"
      />

      <template v-else>
        <ReportCard
          v-for="issue in filteredIssues"
          :key="issue.key"
          :issue="issue"
        />
      </template>
    </v-col>
  </v-row>
</template>

<script>
import _ from "lodash";

import PrintForm from "./PrintForm.vue";
import ReportFilter from "./Filter.vue";
import ReportCard from "./ReportCard.vue";
import Totals from "./Totals.vue";
import * as reportsApi from "@/backend/reportsApi.js";
import DownloadFile from "@/components/DowloadFile.vue";
import PrintModeBtn from "@/components/PrintModeBtn.vue";
import Search from "@/components/Search.vue";
import ReportHeading from "@/components/ReportHeading.vue";
import { columnTypes, numberToString } from "@/helpers";

export default {
  components: {
    PrintForm,
    ReportFilter,
    ReportCard,
    Totals,
    DownloadFile,
    PrintModeBtn,
    Search,
    ReportHeading,
  },
  data: () => ({
    loading: false,
    issues: [],
    filter: {
      dateFrom: "",
      dateTo: "",
      project: null,
      client: null,
    },
    printMode: false,
    totalPayed: 0,
    totalSpent: 0,
    search: "",
    authorFilter: "",
    employeeFilter: "",
  }),
  computed: {
    filteredIssues() {
      let issues = this.issues;

      if (this.search) {
        issues = issues.filter((item) => item.key.includes(this.search));
      }

      if (this.authorFilter) {
        issues = issues.filter((item) => item.author === this.authorFilter);
      }

      if (this.employeeFilter) {
        issues = issues.filter((item) => {
          return item.worklog.some((w) => w.employee === this.employeeFilter);
        });
      }

      return issues.sort((a, b) => b.numberedKey - a.numberedKey);
    },
    specificationData() {
      const report = {
        rows: this.issues.map((i) => {
          const payedHours =
            i.worklog.reduce((sum, n) => sum + n.payedSeconds, 0) / 3600;
          const spentHours =
            i.worklog.reduce((sum, n) => sum + n.timeSpentSeconds, 0) / 3600;
          return [
            i.key,
            i.summary,
            i.status,
            numberToString(spentHours.toFixed(4)),
            numberToString(payedHours.toFixed(4)),
            numberToString((payedHours * 500).toFixed(4)),
            numberToString((payedHours * 500 * 1.2).toFixed(4)),
          ];
        }),
        columns: [
          "Номер",
          "Завдання (зміст наданих послуг)",
          "Статус",
          "Години без урахування рівня співробітника",
          "Години з урахуванням рівня співробітника",
          "Вартість за базовим тарифом, без ПДВ",
          "Вартість за базовим тарифом, з ПДВ",
        ],
        columnTypes: [
          columnTypes.STRING,
          columnTypes.STRING,
          columnTypes.STRING,
          columnTypes.NUMBER,
          columnTypes.NUMBER,
          columnTypes.NUMBER,
          columnTypes.NUMBER,
        ],
      };
      const amountCostVat = report.rows.reduce(
        (accumulator, currentValue) => accumulator + +currentValue[6],
        0
      );
      const amountCost = report.rows.reduce(
        (accumulator, currentValue) => accumulator + +currentValue[5],
        0
      );

      const amountPayedHours = this.issues.reduce((total, i) => {
        const issueTotal = i.worklog.reduce((total, w) => {
          return total + w.payedHours;
        }, 0);
        return total + issueTotal;
      }, 0);

      const amountSpentHours =
        this.issues.reduce((total, i) => {
          const issueTotal = i.worklog.reduce((sum, w) => {
            return sum + w.timeSpentSeconds;
          }, 0);
          return total + issueTotal;
        }, 0) / 3600;

      report.rows.push([
        "",
        "",
        "Загалом:",
        numberToString(amountSpentHours.toFixed(2)),
        numberToString(amountPayedHours.toFixed(2)),
        numberToString(amountCost.toFixed(2)),
        numberToString(amountCostVat.toFixed(2)),
      ]);

      return report;
    },
    reportData() {
      let reportData = [];
      this.issues.forEach((i) => {
        reportData = reportData.concat(
          i.worklog.map((w) => {
            const htmlDescription = i.description;
            const tempDiv = document.createElement("div");
            tempDiv.innerHTML = htmlDescription;
            return [
              i.key,
              i.summary,
              tempDiv.textContent || tempDiv.innerText,
              w.employee,
              w.date,
              w.comment,
              numberToString((w.timeSpentSeconds / 3600).toFixed(4)),
              numberToString((w.payedSeconds / 3600).toFixed(4)),
            ];
          })
        );
      });

      return {
        rows: reportData,
        columns: [
          "Номер",
          "Завдання (зміст наданих послуг)",
          "Опис задачі",
          "Виконавець",
          "Дата виконання",
          "Опис робіт",
          "Години без урахування рівня співробітника",
          "Години з урахуванням рівня співробітника",
        ],
        columnTypes: [
          columnTypes.STRING,
          columnTypes.STRING,
          columnTypes.STRING,
          columnTypes.STRING,
          columnTypes.DATE,
          columnTypes.STRING,
          columnTypes.NUMBER,
          columnTypes.NUMBER,
        ],
      };
    },
    filterReady() {
      return this.filter.dateFrom && this.filter.dateTo && this.filter.client;
    },
    authors() {
      return [...new Set(this.issues.map((i) => i.author))];
    },
    employees() {
      return [
        ...new Set(
          this.issues.reduce((arr, item) => {
            return arr.concat(item.worklog.map((w) => w.employee));
          }, [])
        ),
      ];
    },
    projects() {
      return [
        ...new Set(
          this.issues.reduce((arr, item) => {
            return arr.concat(item.worklog.map((w) => w.project));
          }, [])
        ),
      ];
    },
  },
  watch: {
    filter() {
      this.updateReport();
    },
  },
  methods: {
    async updateReport() {
      this.loading = true;
      this.issues = [];

      try {
        const worklogs = await reportsApi.worklogUpdateReport({
          clientId: this.filter.client.id,
          projectId: this.filter.project.id,
          dateFrom: this.filter.dateFrom,
          dateTo: this.filter.dateTo,
        });

        const groupedByIssue = _.groupBy(worklogs, (w) => w.key);
        _.forIn(groupedByIssue, (g) => {
          this.issues.push({
            key: g[0].key,
            numberedKey: g[0].numberedKey,
            summary: g[0].summary,
            author: g[0].issueAuthor,
            status: g[0].status,
            dueDate: g[0].dueDate,
            description: g[0].description,
            customerDescription: g[0].customerDescription,
            worklog: g.map((i) => {
              return {
                employee: i.employee,
                comment: i.comment,
                date: i.date,
                payedSeconds: i.payedSeconds,
                timeSpentSeconds: i.timeSpentSeconds,
                payedHours: i.payedHours,
                project: i.project,
              };
            }),
          });
        });
      } catch {
        this.$notifyWarning(
          "В даних звіту помилки. Подивіться в Система/Стан БД"
        );
      }
      this.loading = false;
    },
    updateTotalPayed(totalPayedHours) {
      this.totalPayed = totalPayedHours;
    },
    updateTotalSpent(totalTimeSpentHours) {
      this.totalSpent = totalTimeSpentHours;
    },
    changePrintMode() {
      this.printMode = !this.printMode;
    },

    handleFilterReset() {
      if (this.issues.length) {
        this.issues = [];
      }
    },
  },
};
</script>

<style>
.v-tabs__item {
  padding: 0 10px;
}
.v-tabs__container {
  height: 28px;
}
.v-tabs__div {
  text-transform: none;
}
.v-input--selection-controls {
  margin: 0;
  padding: 0;
}
.print-button {
  right: 3px;
}
.v-btn--top:not(.v-btn--absolute) {
  top: 68px;
}
</style>
